import ColorScheme, { getAccessibleTextColor } from 'mini-color-utils/lib/ColorScheme';
import commonStrings from 'owa-strings/owa-mini-common-strings';
import MiniCommonIcons from 'owa-mini-icons/lib/data/MiniCommonIcons';
import React from 'react';
import { CalendarEvent } from 'mini-calendar-event-types';
import { CharmControl } from 'mini-charms';
import { format } from 'owa-strings';
import { getAriaLabelForTimeAndDuration } from 'mini-common-utils/lib/utils/getAriaLabelForTimeAndDuration';
import { getEffectiveCharmId } from 'mini-charms/lib/utils/getEffectiveCharmId';
import { HubItem } from 'mini-hub';
import { mergeStyles } from '@uifabric/styling';
import { renderTimeAndDuration } from 'mini-rsvp/lib/components/TimeAndDuration';

const styles = require('./RsvpEventDetails.scss');
const classNames = require('classnames');

export interface RsvpEventDetailsProps {
    /**
     * The calendar event
     */
    item: CalendarEvent;

    /**
     * Color scheme for the text
     */
    colorScheme: ColorScheme;

    /**
     * Returns text with conflict information
     */
    getConflictsText: () => string;

    /**
     * Element to focus on first
     */
    forwardRef?: React.RefObject<any>;

    /**
     * Boolean to indicate whether day view can be shown or not
     */
    canShowDayView: boolean;

    /**
     * Boolean to indicate whether day view expanded or collapsed
     */
    isDayViewExpanded: boolean;

    /**
     * onClick actions for event details
     */
    onClick: () => void;
}

export const RsvpEventDetails = (props: RsvpEventDetailsProps) => {
    const {
        item,
        colorScheme,
        forwardRef,
        getConflictsText,
        canShowDayView,
        isDayViewExpanded,
        onClick,
    } = props;
    const { Subject, CharmId } = item;
    const eventSubject = Subject || commonStrings.noSubjectText;
    const effectivecharmId = getEffectiveCharmId(CharmId, null, true /* fallback to no charm */);
    const expandCollapseIcon = canShowDayView
        ? isDayViewExpanded
            ? MiniCommonIcons.ChevronUp
            : MiniCommonIcons.ChevronDown
        : null;
    const conflictsText = getConflictsText();
    const ariaLabel = format(
        commonStrings.rsvpEventDetailsAriaLabel,
        eventSubject,
        getAriaLabelForTimeAndDuration(item),
        conflictsText,
        canShowDayView
            ? isDayViewExpanded
                ? commonStrings.rsvpDayViewCollapseAriaLabel
                : commonStrings.rsvpDayViewExpandAriaLabel
            : ''
    );

    const renderEventDetails = () => (
        <div className={styles.textContainer} aria-hidden="true">
            <span className={styles.titleText}>{eventSubject}</span>
            {renderTimeAndDuration({ item })}
            <div
                className={classNames(
                    styles.conflictsText,
                    mergeStyles({ color: getAccessibleTextColor(colorScheme) })
                )}>
                {conflictsText}
            </div>
        </div>
    );

    const renderCharm = () => (
        <CharmControl
            charmId={effectivecharmId}
            color={getAccessibleTextColor(colorScheme)}
            ariaHidden
        />
    );

    return (
        <HubItem
            id={'RsvpEventDetails'}
            ariaLabel={ariaLabel}
            forwardRef={forwardRef}
            onClick={onClick}
            onRenderIcon={renderCharm}
            onRenderText={renderEventDetails}
            additionalInfoIcon={expandCollapseIcon}
            styles={{
                className: styles.eventDetailsHubItem,
                iconContainerStyles: styles.iconContainer,
                additionalInfoStyles: styles.chevronIcon,
            }}
        />
    );
};
